import { CATEGORIES, CATEGORY_META, primaryCategory, entropy01 } from "./categories.js";
import { THEME_ORDER, THEMES } from "./themes.js";

export function createUI() {
  const panel = document.querySelector("#detail-panel");
  const wordEl = document.querySelector("#detail-word");
  const primaryEl = document.querySelector("#detail-primary");
  const ambiguityEl = document.querySelector("#detail-ambiguity");
  const barsEl = document.querySelector("#detail-bars");
  const metaEl = document.querySelector("#detail-meta");
  const followHint = document.querySelector("#follow-hint");
  const searchInput = document.querySelector("#search-input");
  const searchResults = document.querySelector("#search-results");
  const legend = document.querySelector("#legend");
  const countEl = document.querySelector("#hud-count");
  const fpsEl = document.querySelector("#hud-fps");
  const hoverEl = document.querySelector("#hover-word");
  const closeBtn = document.querySelector("#detail-close");
  const themeRail = document.querySelector("#theme-rail");
  const allBtn = document.querySelector("#filter-all");
  let currentColors = Object.fromEntries(CATEGORIES.map((key) => [key, CATEGORY_META[key].color]));
  let legendCounts = Object.fromEntries(CATEGORIES.map((key) => [key, 0]));

  function setCounts(n) {
    countEl.textContent = n.toLocaleString();
  }

  function setFps(fps) {
    fpsEl.textContent = String(fps);
  }

  function setHover(word) {
    if (!word) {
      hoverEl.hidden = true;
      hoverEl.textContent = "";
      return;
    }
    hoverEl.hidden = false;
    hoverEl.textContent = word;
  }

  function openDetail(row, extra, colorMap = currentColors) {
    panel.classList.add("is-open");
    wordEl.textContent = row.word.replaceAll("_", " ");
    const primary = primaryCategory(row.scores);
    primaryEl.textContent = CATEGORY_META[primary].label;
    primaryEl.style.background = colorMap[primary];
    const amb = Math.round(entropy01(row.scores) * 100);
    ambiguityEl.textContent = `${amb}% mixed`;
    barsEl.replaceChildren();
    const ranked = CATEGORIES
      .map((key) => ({ key, value: row.scores[key] }))
      .filter((item) => item.value >= 0.4)
      .sort((a, b) => b.value - a.value);
    for (const item of ranked) {
      const rowEl = document.createElement("div");
      rowEl.className = "bar-row";
      const label = document.createElement("span");
      label.className = "bar-label";
      label.textContent = CATEGORY_META[item.key].label;
      const track = document.createElement("div");
      track.className = "bar-track";
      const fill = document.createElement("div");
      fill.className = "bar-fill";
      fill.style.width = `${item.value}%`;
      fill.style.background = colorMap[item.key];
      track.appendChild(fill);
      const value = document.createElement("span");
      value.className = "bar-value";
      value.textContent = `${item.value.toFixed(1)}%`;
      rowEl.append(label, track, value);
      barsEl.appendChild(rowEl);
    }
    metaEl.textContent = extra;
    followHint.hidden = false;
  }

  function closeDetail() {
    panel.classList.remove("is-open");
    followHint.hidden = true;
  }

  function bindSearch(rows, onPick) {
    searchInput.addEventListener("input", () => {
      const q = searchInput.value.trim().toLowerCase().replaceAll(" ", "_");
      searchResults.replaceChildren();
      if (q.length < 2) {
        searchResults.hidden = true;
        return;
      }
      const hits = [];
      for (let i = 0; i < rows.length && hits.length < 12; i += 1) {
        if (rows[i].word.includes(q)) hits.push(i);
      }
      if (!hits.length) {
        searchResults.hidden = true;
        return;
      }
      searchResults.hidden = false;
      for (const index of hits) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.textContent = rows[index].word.replaceAll("_", " ");
        btn.addEventListener("click", () => {
          searchResults.hidden = true;
          searchInput.value = rows[index].word.replaceAll("_", " ");
          onPick(index);
        });
        searchResults.appendChild(btn);
      }
    });
  }

  function bindLegend(rows, onToggleCategory, onShowAll) {
    legendCounts = Object.fromEntries(CATEGORIES.map((key) => [key, 0]));
    for (const row of rows) legendCounts[primaryCategory(row.scores)] += 1;
    legend.replaceChildren();
    allBtn.addEventListener("click", onShowAll);
    for (const key of CATEGORIES) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "legend-item";
      btn.dataset.category = key;
      const swatch = document.createElement("span");
      swatch.className = "swatch";
      swatch.style.background = currentColors[key];
      const name = document.createElement("span");
      name.textContent = CATEGORY_META[key].label;
      const n = document.createElement("em");
      n.textContent = legendCounts[key].toLocaleString();
      btn.append(swatch, name, n);
      btn.addEventListener("click", () => onToggleCategory(key));
      legend.appendChild(btn);
    }
  }

  function setLegendState(activeSet, colorMap) {
    currentColors = colorMap;
    const showAll = !activeSet || activeSet.size === 0 || activeSet.size === CATEGORIES.length;
    allBtn.classList.toggle("is-active", showAll);
    for (const btn of legend.querySelectorAll(".legend-item")) {
      const key = btn.dataset.category;
      const on = showAll || activeSet.has(key);
      btn.classList.toggle("is-off", !on);
      const swatch = btn.querySelector(".swatch");
      if (swatch) swatch.style.background = colorMap[key];
    }
  }

  function bindThemes(activeId, onPick) {
    themeRail.replaceChildren();
    for (const id of THEME_ORDER) {
      const theme = THEMES[id];
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "theme-chip";
      btn.dataset.theme = id;
      btn.title = `${theme.label} — ${theme.blurb}`;
      const dot = document.createElement("span");
      dot.className = "theme-dot";
      dot.style.background = theme.ui.swatch;
      const label = document.createElement("span");
      label.textContent = theme.label;
      btn.append(dot, label);
      btn.classList.toggle("is-active", id === activeId);
      btn.addEventListener("click", () => onPick(id));
      themeRail.appendChild(btn);
    }
  }

  function setActiveTheme(id) {
    for (const btn of themeRail.querySelectorAll(".theme-chip")) {
      btn.classList.toggle("is-active", btn.dataset.theme === id);
    }
  }

  function bindCamera(onZoomIn, onZoomOut, onOverview) {
    document.querySelector("#cam-in").addEventListener("click", onZoomIn);
    document.querySelector("#cam-out").addEventListener("click", onZoomOut);
    document.querySelector("#cam-home").addEventListener("click", onOverview);
  }

  closeBtn.addEventListener("click", closeDetail);

  return {
    setCounts,
    setFps,
    setHover,
    openDetail,
    closeDetail,
    bindSearch,
    bindLegend,
    setLegendState,
    bindThemes,
    setActiveTheme,
    bindCamera,
  };
}
